import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Building2, Loader2, ShieldCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import RiskMeter from '../components/RiskMeter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SharedReport = () => {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/shared-report/${shareToken}`).then(res => { setData(res.data); }).catch(() => setError('Report not found or link expired')).finally(() => setLoading(false));
  }, [shareToken]);

  if (loading) return <div className="min-h-screen gradient-bg flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-cyan-400" /></div>;
  if (error) return (
    <div className="min-h-screen gradient-bg flex items-center justify-center">
      <div className="glass-card text-center max-w-md">
        <AlertTriangle className="h-12 w-12 text-amber-400 mx-auto mb-4" />
        <p className="text-white text-lg mb-2">{error}</p>
        <Link to="/" className="text-cyan-400 hover:underline text-sm">Go to PropertyCheck AI</Link>
      </div>
    </div>
  );

  const report = data?.report;
  const riskColor = report?.risk_score >= 80 ? 'emerald' : report?.risk_score >= 50 ? 'amber' : 'red';

  return (
    <div className="min-h-screen gradient-bg">
      <div className="fixed inset-0 grid-bg opacity-30 pointer-events-none" />
      <nav className="glass-header sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center gap-2">
          <Link to="/" className="flex items-center gap-2">
            <Building2 className="h-8 w-8 text-cyan-400" />
            <span className="text-xl font-semibold text-white" style={{ fontFamily: 'Playfair Display' }}>PropertyCheck AI</span>
          </Link>
          <Badge variant="outline" className="ml-auto border-cyan-500/30 text-cyan-400">Shared Report</Badge>
        </div>
      </nav>
      <main className="max-w-4xl mx-auto px-4 py-8 relative z-10">
        {report && (
          <div className="space-y-6">
            <div className="glass-card">
              <h1 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display' }}>Property Verification Report</h1>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Property ID: {data.property_id}</p>
                  <p className="text-xs text-slate-500">Generated: {new Date(data.created_at).toLocaleString()}</p>
                </div>
                <div className="text-center">
                  <RiskMeter score={report.risk_score} />
                  <p className={`text-2xl font-bold mt-2 text-${riskColor}-400`}>{report.risk_score}/100</p>
                  <p className={`text-sm text-${riskColor}-400`}>{report.risk_score >= 80 ? 'Low Risk' : report.risk_score >= 50 ? 'Medium Risk' : 'High Risk'}</p>
                </div>
              </div>
            </div>
            {report.summary && <div className="glass-card"><h3 className="text-lg font-semibold text-white mb-2">Executive Summary</h3><p className="text-sm text-slate-300 leading-relaxed">{report.summary}</p></div>}
            {report.risk_factors && (
              <div className="glass-card">
                <h3 className="text-lg font-semibold text-white mb-4">Risk Factors</h3>
                {report.risk_factors.map((f, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 mb-2 bg-slate-800/50 rounded-lg">
                    {f.severity === 'HIGH' ? <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5" /> : <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5" />}
                    <div><p className="text-sm text-white">{f.factor || f.name}</p><p className="text-xs text-slate-400">{f.detail || f.description}</p></div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-500 text-center">This report was generated by PropertyCheck AI. For full verification, visit <Link to="/" className="text-cyan-400 hover:underline">propertycheck.ai</Link></p>
          </div>
        )}
      </main>
    </div>
  );
};

export default SharedReport;
